import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

// How long a row shows "Added" after its product reaches the cart
const ADDED_STATE_DURATION = 2000;

// How long an error stays under a row, matching the product form
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// How long an announcement stays in the live region
const ANNOUNCEMENT_DURATION = 5000;

/**
 * @typedef {object} PairItWithRefs
 * @property {HTMLElement} liveRegion - Announces adds and errors that the product form doesn't announce.
 */

/**
 * The "Pair it with" list on the product page (`blocks/pair-it-with.liquid`).
 *
 * Each row adds its own product with the theme's existing components: `product-form-component`
 * for products without options, and `quick-add-component` to choose options in the quick-add
 * modal first. This component allows one request per row at a time and shows each row's
 * loading, added, and error states.
 *
 * @extends {Component<PairItWithRefs>}
 */
class PairItWithComponent extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<HTMLElement, number>} */
  #stateTimeouts = new Map();

  /** @type {Map<HTMLElement, number>} */
  #errorTimeouts = new Map();

  /** @type {number | undefined} */
  #announcementTimeout;

  /** Whether a row is loading the quick-add modal, which shows one product at a time */
  #isLoadingOptions = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleQuickAddCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;

    for (const timeout of [...this.#stateTimeouts.values(), ...this.#errorTimeouts.values()]) {
      clearTimeout(timeout);
    }
    this.#stateTimeouts.clear();
    this.#errorTimeouts.clear();
    clearTimeout(this.#announcementTimeout);
  }

  /**
   * Adds a row's product to the cart unless that row is already adding it.
   * Bound with `on:submit` on the row's `product-form-component`.
   *
   * @param {SubmitEvent} event - The submit event, targeting the row's product form component.
   */
  handleSubmit(event) {
    const productForm = event.target;
    const item = this.#getItem(productForm);

    // Until product-form.js has loaded, let the form post to the cart natively
    if (!item || !isProductForm(productForm)) return;

    event.preventDefault();
    if (item.dataset.state === 'loading') return;

    /** @type {{ event: import('@shopify/events').CartLinesUpdateEvent | null }} */
    const dispatched = { event: null };
    const captureCartEvent = (/** @type {Event} */ cartEvent) => {
      dispatched.event = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (cartEvent);
    };

    // The product form dispatches its cart event synchronously, before the request starts
    productForm.addEventListener(StandardEvents.cartLinesUpdate, captureCartEvent, { once: true });
    try {
      productForm.handleSubmit(event);
    } finally {
      productForm.removeEventListener(StandardEvents.cartLinesUpdate, captureCartEvent);
    }

    // No event means the form declined to submit, for example when quantity rules aren't met
    const promise = dispatched.event?.promise;
    if (!promise) return;

    this.#hideError(item);
    this.#setState(item, 'loading');

    promise
      .then((result) => {
        // The product form shows and announces cart errors, such as stock limits, under the row
        if (result?.detail?.didError) {
          this.#setState(item, 'idle');
          return;
        }

        this.#showAdded(item);
        this.#announce(item.dataset.addedMessage);
      })
      .catch((error) => {
        this.#setState(item, 'idle');
        if (error?.name === 'AbortError') return;

        this.#showError(item);
      });
  }

  /**
   * Opens the quick-add modal so options can be chosen before the row's product is added.
   * Bound with `on:click` on the Add button of rows whose product has options.
   *
   * @param {MouseEvent} event - The click event, targeting the row's Add button.
   */
  async handleChooseOptions(event) {
    const item = this.#getItem(event.target);
    if (!item) return;

    event.preventDefault();
    if (this.#isLoadingOptions || item.dataset.state === 'loading') return;

    const quickAdd = item.querySelector('quick-add-component');
    if (!isQuickAdd(quickAdd) || !document.getElementById('quick-add-dialog')) {
      // Quick add is turned off in theme settings, so options are chosen on the product page
      const link = item.querySelector('a[href]');
      if (link instanceof HTMLAnchorElement) window.location.assign(link.href);
      return;
    }

    this.#isLoadingOptions = true;
    this.#hideError(item);
    this.#setState(item, 'loading');

    try {
      await quickAdd.handleClick(event);
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        this.#showError(item);
      }
    } finally {
      this.#isLoadingOptions = false;
      if (item.dataset.state === 'loading') this.#setState(item, 'idle');
    }
  }

  /**
   * Shows "Added" on a row once its product is added from the quick-add modal.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleQuickAddCartUpdate = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('quick-add-dialog')) return;

    event.promise
      ?.then((result) => {
        const productId = result?.detail?.productId;
        if (result?.detail?.didError || !productId) return;

        for (const item of this.querySelectorAll('.pair-it-with__card')) {
          if (item instanceof HTMLElement && item.dataset.productId === String(productId)) {
            this.#showAdded(item);
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pair-it-with] Event promise rejected:', error);
      });
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null} The row that contains the target.
   */
  #getItem(target) {
    if (!(target instanceof Element)) return null;

    const item = target.closest('.pair-it-with__card');
    return item instanceof HTMLElement && this.contains(item) ? item : null;
  }

  /**
   * @param {HTMLElement} item
   * @param {'idle' | 'loading' | 'added'} state
   */
  #setState(item, state) {
    clearTimeout(this.#stateTimeouts.get(item));
    this.#stateTimeouts.delete(item);

    item.dataset.state = state;

    const button = item.querySelector('.pair-it-with__button');
    if (!button) return;

    if (state === 'loading') {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  }

  /** @param {HTMLElement} item */
  #showAdded(item) {
    this.#setState(item, 'added');
    this.#stateTimeouts.set(
      item,
      setTimeout(() => this.#setState(item, 'idle'), ADDED_STATE_DURATION)
    );
  }

  /** @param {HTMLElement} item */
  #showError(item) {
    const message = this.dataset.errorMessage ?? '';
    const error = item.querySelector('.pair-it-with__error');

    if (error) {
      // Same structure the product form writes to: the icon, then the message text node
      const textNode = error.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        error.append(message);
      }

      error.classList.remove('hidden');
      clearTimeout(this.#errorTimeouts.get(item));
      this.#errorTimeouts.set(
        item,
        setTimeout(() => this.#hideError(item), ERROR_MESSAGE_DISPLAY_DURATION)
      );
    }

    this.#announce(message);
  }

  /** @param {HTMLElement} item */
  #hideError(item) {
    clearTimeout(this.#errorTimeouts.get(item));
    this.#errorTimeouts.delete(item);
    item.querySelector('.pair-it-with__error')?.classList.add('hidden');
  }

  /** @param {string | undefined} message */
  #announce(message) {
    if (!message) return;

    const { liveRegion } = this.refs;
    clearTimeout(this.#announcementTimeout);

    // Clear first so a repeated message is announced again
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });

    this.#announcementTimeout = setTimeout(() => {
      liveRegion.textContent = '';
    }, ANNOUNCEMENT_DURATION);
  }
}

/**
 * @param {EventTarget | null} element
 * @returns {element is HTMLElement & { handleSubmit: (event: Event) => void }}
 */
function isProductForm(element) {
  return element instanceof HTMLElement && typeof (/** @type {any} */ (element).handleSubmit) === 'function';
}

/**
 * @param {Element | null} element
 * @returns {element is HTMLElement & { handleClick: (event: Event) => Promise<void> }}
 */
function isQuickAdd(element) {
  return element instanceof HTMLElement && typeof (/** @type {any} */ (element).handleClick) === 'function';
}

if (!customElements.get('pair-it-with-component')) {
  customElements.define('pair-it-with-component', PairItWithComponent);
}
